import { useEffect, useRef, useState, KeyboardEvent } from "react";
import {
  suggestSearchKeys,
  SearchSuggestItem,
} from "@services/krent.service";

export type { SearchSuggestItem };

export const useSearchSuggestions = (keyword: string) => {
  const [items, setItems] = useState<SearchSuggestItem[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const requestId = useRef(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trimmed = keyword.trim();
    if (trimmed.length < 2) {
      requestId.current += 1;
      setItems([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    const abort = new AbortController();
    const timer = window.setTimeout(async () => {
      const id = ++requestId.current;
      const data = await suggestSearchKeys(trimmed, abort.signal);
      if (abort.signal.aborted || id !== requestId.current) return;
      setItems(data);
      setOpen(data.length > 0);
      setActiveIndex(-1);
    }, 300);

    return () => {
      window.clearTimeout(timer);
      abort.abort();
    };
  }, [keyword]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return false;
    }
    if (!open || items.length === 0) return false;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
      return true;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        index <= 0 ? items.length - 1 : index - 1,
      );
      return true;
    }
    if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      return items[activeIndex];
    }
    return false;
  };

  return {
    items,
    open,
    setOpen,
    activeIndex,
    setActiveIndex,
    wrapRef,
    onKeyDown,
  };
};
